/**
 * Чистые функции для панели «Бот на встрече» (bit-meetbot): перевод состояния
 * встречи и кода отказа /join в понятный пользователю русский текст. Сетевой
 * части здесь нет — она в components/MeetBotJoin.js.
 */

// Состояния — ровно те, что отдаёт GET /meetings/{id} (bit-meetbot/app/storage.py).
// WAITING_ROOM сформулирован отдельно и подробно: это самая частая причина,
// по которой пользователю кажется, что «ничего не происходит» — бот уже
// стучится, но организатор его не впустил.
const STATE_TEXT = {
  JOINING: 'Бот подключается к встрече…',
  WAITING_ROOM: 'Бот стучится в комнату ожидания — организатор ещё не впустил его. '
    + 'Впустите бота, чтобы началась запись.',
  RECORDING: 'Идёт запись встречи',
  // Обещаем ровно то, что кладём в Историю: расшифровку с именами. Саммари
  // на этом пути не делается, вкладки «Протокол» у такой записи не будет —
  // раньше оба текста отправляли человека искать её напрасно.
  PROCESSING: 'Встреча закончилась, готовим расшифровку…',
  DONE: 'Готово — расшифровка с именами участников появилась в Истории',
  ERROR: 'Не удалось записать встречу',
};

/** Человеческий текст состояния встречи. error — поле ответа, показывается на ERROR. */
export function describeMeetingState(state, error) {
  if (state === 'ERROR') return error || STATE_TEXT.ERROR;
  return STATE_TEXT[state] || 'Статус неизвестен';
}

/** Встреча ещё в работе: есть смысл продолжать опрос и показывать «Остановить». */
// Бот сидит в комнате — подпись экрана и запасной ответ, если сервис ещё не
// сказал своего (до первого опроса после перезагрузки, старый образ бота).
const IN_ROOM = ['JOINING', 'WAITING_ROOM', 'RECORDING'];

/**
 * Можно ли остановить встречу. Решает сервис (`can_stop`); список состояний —
 * только запасной путь, когда поля в ответе нет: без него бота в комнате было
 * бы нечем остановить.
 */
export function meetingCanStop(meeting) {
  if (!meeting) return false;
  if (typeof meeting.can_stop === 'boolean') return meeting.can_stop;
  return IN_ROOM.includes(meeting.state);
}

/** Пустит ли сервис новую встречу сейчас (`can_start_new`, иначе — по комнате). */
export function meetingCanStartNew(meeting) {
  if (!meeting) return false;
  if (typeof meeting.can_start_new === 'boolean') return meeting.can_start_new;
  return Boolean(meeting.state) && !IN_ROOM.includes(meeting.state);
}

export function isMeetingActive(state) {
  return state === 'JOINING' || state === 'WAITING_ROOM'
    || state === 'RECORDING' || state === 'PROCESSING';
}

/**
 * Что делать с очередным ответом опроса встречи: продолжать или прекратить.
 *
 * Вынесено сюда, а не оставлено в экране, ровно по одной причине: 21.09.2026
 * экран опрашивал сервис каждые три секунды по встрече, упавшей за семнадцать
 * секунд, — больше двадцати минут подряд, — а после перезапуска сервиса тем же
 * темпом получал 404 без конца. Условие остановки должно быть местом, куда
 * можно ткнуть пальцем и проверить, а не строчкой внутри useCallback.
 *
 * @param {{data?: {state?: string}, error?: {response?: {status?: number}}}} result
 * @returns {{stop: boolean, patch: object|null}} patch — что дописать встрече.
 */
export function meetingPollDecision({ data, error } = {}) {
  if (error) {
    // 404 — встречи в сервисе больше нет: состояние встреч живёт в памяти, и
    // перезапуск его теряет. Молчать нельзя: человек останется с ожиданием,
    // которое не кончится никогда.
    if (error?.response?.status === 404) {
      // Кнопки — тоже здесь: сервис, который их решал, встречу уже не знает.
      return { stop: true, patch: { state: 'ERROR', can_stop: false, can_start_new: true,
        error: 'Запись прервалась: сервис встреч перезапустился' } };
    }
    // Прочее — одиночный сбой сети, живую встречу из-за него не бросаем.
    return { stop: false, patch: null };
  }
  return { stop: !isMeetingActive(data?.state), patch: null };
}

// Тексты по умолчанию на случай, если бэкенд ответил без detail (сеть/шлюз
// оборвали тело) — тогда пользователь не должен увидеть "Request failed with
// status code 400" из axios.
const JOIN_ERROR_FALLBACK = {
  400: 'Не понимаю эту ссылку на встречу',
  429: 'Сейчас слишком много активных встреч, попробуйте позже',
  402: 'Квота исчерпана. Обратитесь к администратору',
  403: 'Квота исчерпана. Обратитесь к администратору',
};

/** Текст отказа POST /join: detail от сервера, если он есть, иначе — по коду ответа. */
export function describeJoinError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  // Только строка: текст уходит в разметку как есть, а 422 от FastAPI несёт список.
  if (detail && typeof detail === 'string') return detail;
  if (status && JOIN_ERROR_FALLBACK[status]) return JOIN_ERROR_FALLBACK[status];
  return 'Не удалось подключить бота ко встрече. Попробуйте позже';
}
